/**
 * Step 5: Client Identity
 * "Continuer sans créer de compte" / "Créer un compte" / "Connexion"
 * With country code selector for phone
 */
import React, { useState } from 'react';
import { useBooking } from '../../context/BookingContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { 
  User, Mail, Phone, LogIn, UserPlus, ArrowRight, 
  ArrowLeft, AlertCircle, Check
} from 'lucide-react';
import PhoneInputWithCountry from './PhoneInputWithCountry';

const Step5Identity = () => {
  const { bookingData, updateBookingData, nextStep, prevStep } = useBooking();
  const { user, isAuthenticated, login, register } = useAuth();
  
  const [mode, setMode] = useState(isAuthenticated ? 'logged' : 'guest');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    phone_country_code: '+33'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // If already authenticated, pre-fill data
  React.useEffect(() => {
    if (isAuthenticated && user) {
      updateBookingData({
        client_email: user.email,
        client_name: `${user.first_name} ${user.last_name}`,
        client_phone: user.phone || '',
        phone_country_code: user.phone_country_code || '+33'
      });
    }
  }, [isAuthenticated, user]);

  const validateGuest = () => {
    const errors = {};
    
    if (!bookingData.client_name || bookingData.client_name.length < 2) {
      errors.client_name = "Nom complet requis";
    }
    if (!bookingData.client_email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingData.client_email)) {
      errors.client_email = "Email valide requis";
    }
    // Validate phone without country code prefix
    const phoneDigits = (bookingData.client_phone || '').replace(/\s/g, '');
    if (!phoneDigits || phoneDigits.length < 6) {
      errors.client_phone = "Téléphone valide requis";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const loggedUser = await login(formData.email, formData.password);
      updateBookingData({
        client_email: loggedUser.email,
        client_name: `${loggedUser.first_name} ${loggedUser.last_name}`,
        client_phone: loggedUser.phone || '',
        phone_country_code: loggedUser.phone_country_code || '+33'
      });
      setMode('logged');
    } catch (err) {
      setError(err.message || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const fullPhone = `${formData.phone_country_code} ${formData.phone}`;
      const newUser = await register({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: fullPhone,
        phone_country_code: formData.phone_country_code
      });
      updateBookingData({
        client_email: newUser.email,
        client_name: `${newUser.first_name} ${newUser.last_name}`,
        client_phone: newUser.phone || fullPhone,
        phone_country_code: formData.phone_country_code
      });
      setMode('logged');
    } catch (err) {
      setError(err.message || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (mode === 'guest') {
      if (!validateGuest()) return;
      // Combine country code with phone for storage
      const fullPhone = `${bookingData.phone_country_code || '+33'} ${bookingData.client_phone}`;
      updateBookingData({ client_phone_full: fullPhone });
    }
    nextStep();
  };

  // Already logged in
  if (mode === 'logged' || isAuthenticated) {
    return (
      <div className="space-y-6" data-testid="booking-step-5">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-['Playfair_Display'] text-[#E5E5E5] mb-2">
            Vos coordonnées
          </h2>
          <p className="text-[#E5E5E5]/60 text-sm">
            Informations de contact confirmées
          </p>
        </div>

        <Card className="bg-[#1F1F1F] border-[#C9A24D]/30">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-[#C9A24D]/20 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-[#C9A24D]" />
              </div>
              <div>
                <p className="text-[#E5E5E5] font-medium">{bookingData.client_name || user?.first_name + ' ' + user?.last_name}</p>
                <p className="text-[#E5E5E5]/60 text-sm">{bookingData.client_email || user?.email}</p>
              </div>
              <Check className="w-6 h-6 text-green-400 ml-auto" />
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-[#E5E5E5]/60">
                <Mail className="w-4 h-4" />
                <span>{bookingData.client_email || user?.email}</span>
              </div>
              <div className="flex items-center gap-2 text-[#E5E5E5]/60">
                <Phone className="w-4 h-4" />
                <span>{bookingData.client_phone_full || bookingData.client_phone || user?.phone || 'Non renseigné'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={prevStep}
            className="flex-1 border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <Button
            onClick={handleNext}
            className="flex-1 bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
            data-testid="step5-next-button"
          >
            Continuer
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="booking-step-5">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-['Playfair_Display'] text-[#E5E5E5] mb-2">
          Vos coordonnées
        </h2>
        <p className="text-[#E5E5E5]/60 text-sm">
          Entrez vos informations pour continuer
        </p>
      </div>

      {/* Mode Tabs */}
      <div className="flex flex-col gap-2">
        <button
          onClick={() => setMode('guest')}
          className={`w-full py-3 px-4 rounded-lg text-left font-medium transition-all border ${
            mode === 'guest'
              ? 'bg-[#C9A24D]/10 border-[#C9A24D] text-[#C9A24D]'
              : 'bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5]/70 hover:border-[#C9A24D]/40'
          }`}
        >
          <div className="flex items-center gap-3">
            <User className="w-5 h-5" />
            <div>
              <span className="block">Continuer sans créer de compte</span>
              <span className="text-xs opacity-60 font-normal">Réservez rapidement avec votre email</span>
            </div>
          </div>
        </button>
        
        <button
          onClick={() => setMode('register')}
          className={`w-full py-3 px-4 rounded-lg text-left font-medium transition-all border ${
            mode === 'register'
              ? 'bg-[#C9A24D]/10 border-[#C9A24D] text-[#C9A24D]'
              : 'bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5]/70 hover:border-[#C9A24D]/40'
          }`}
        >
          <div className="flex items-center gap-3">
            <UserPlus className="w-5 h-5" />
            <div>
              <span className="block">Créer un compte</span>
              <span className="text-xs opacity-60 font-normal">Suivez vos réservations et gagnez du temps</span>
            </div>
          </div>
        </button>
        
        <button
          onClick={() => setMode('login')}
          className={`w-full py-3 px-4 rounded-lg text-left font-medium transition-all border ${
            mode === 'login'
              ? 'bg-[#C9A24D]/10 border-[#C9A24D] text-[#C9A24D]'
              : 'bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5]/70 hover:border-[#C9A24D]/40'
          }`}
        >
          <div className="flex items-center gap-3">
            <LogIn className="w-5 h-5" />
            <div>
              <span className="block">Connexion</span>
              <span className="text-xs opacity-60 font-normal">J'ai déjà un compte</span>
            </div>
          </div>
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Guest Form (Continuer sans créer de compte) */}
      {mode === 'guest' && (
        <div className="space-y-4 animate-in slide-in-from-top-2">
          <div className="p-3 bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-lg">
            <p className="text-[#C9A24D] text-sm">
              ✨ Vous recevrez une confirmation par email avec votre numéro de réservation.
            </p>
          </div>
          
          <div className="space-y-2">
            <Label className="text-[#E5E5E5]">Nom complet *</Label>
            <Input
              type="text"
              value={bookingData.client_name || ''}
              onChange={(e) => updateBookingData({ client_name: e.target.value })}
              placeholder="Jean Dupont"
              className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D]"
              data-testid="guest-name-input"
            />
            {validationErrors.client_name && (
              <p className="text-red-400 text-sm">{validationErrors.client_name}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label className="text-[#E5E5E5]">Email *</Label>
            <Input
              type="email"
              value={bookingData.client_email || ''}
              onChange={(e) => updateBookingData({ client_email: e.target.value })}
              placeholder="votre@email.fr"
              className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D]"
              data-testid="guest-email-input"
            />
            {validationErrors.client_email && (
              <p className="text-red-400 text-sm">{validationErrors.client_email}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label className="text-[#E5E5E5]">Téléphone *</Label>
            <PhoneInputWithCountry
              value={bookingData.client_phone || ''}
              onChange={(val) => updateBookingData({ client_phone: val })}
              countryCode={bookingData.phone_country_code || '+33'}
              onCountryCodeChange={(code) => updateBookingData({ phone_country_code: code })}
              placeholder="6 12 34 56 78"
              data-testid="guest-phone-input"
            />
            {validationErrors.client_phone && (
              <p className="text-red-400 text-sm">{validationErrors.client_phone}</p>
            )}
          </div>
        </div>
      )}

      {/* Login Form */}
      {mode === 'login' && (
        <form onSubmit={handleLogin} className="space-y-4 animate-in slide-in-from-top-2">
          <div className="space-y-2">
            <Label className="text-[#E5E5E5]">Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="votre@email.fr"
              required
              className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#E5E5E5]">Mot de passe</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="••••••••"
              required
              className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D]"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </Button>
        </form>
      )}

      {/* Register Form */}
      {mode === 'register' && (
        <form onSubmit={handleRegister} className="space-y-4 animate-in slide-in-from-top-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#E5E5E5]">Prénom</Label>
              <Input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
                className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#E5E5E5]">Nom</Label>
              <Input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
                className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D]"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#E5E5E5]">Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#E5E5E5]">Téléphone</Label>
            <PhoneInputWithCountry
              value={formData.phone}
              onChange={(val) => setFormData({ ...formData, phone: val })}
              countryCode={formData.phone_country_code}
              onCountryCodeChange={(code) => setFormData({ ...formData, phone_country_code: code })}
              placeholder="6 12 34 56 78"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#E5E5E5]">Mot de passe</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={8}
              className="bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D]"
            />
            <p className="text-xs text-[#E5E5E5]/40">Min. 8 caractères, 1 majuscule, 1 chiffre</p>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
          >
            {loading ? 'Création...' : 'Créer mon compte'}
          </Button>
        </form>
      )}

      {/* Navigation */}
      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={prevStep}
          className="flex-1 border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        {mode === 'guest' && (
          <Button
            onClick={handleNext}
            className="flex-1 bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold"
            data-testid="step5-next-button"
          >
            Continuer
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default Step5Identity;
